import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useState } from "react";
import { Spin } from "antd";
import { RightOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { H5SubPageShell } from "../../components/common/H5SubPageShell";
import "../../components/common/H5SubPageShell.css";
import { StoreOrderApi } from "../../customer_account/StoreOrderApi";
import type { AppMyStoreOrderRow } from "../../customer_account/StoreOrderType";
import { resolveH5StoreOrderDetailPath } from "../../customer_account/storeOrderDetailUtils";
import {
  filterOrdersWithinThreeYears,
  formatOrderSpecLabel,
  isUnpaidStoreOrder,
  resolveStoreOrderStatusClass,
} from "../../customer_account/storeOrderUtils";
import { spaceHomeOrderListCssVars } from "../home/Config";
import { formatApiDateTime } from "../../utils/Backend";
import { h5NavigateTo } from "../../utils/miniProgramEnv";
import { resolveAppMediaUrl } from "../../utils/resolveAppMediaUrl";
import { useRequireLoginRedirect } from "../../utils/useRequireLoginRedirect";
import "./SpaceMineStyle.css";

export const SpaceMineOrdersPage = observer(() => {
  const { spaceId } = useParams<{ spaceId: string }>();
  const navigate = useNavigate();
  const canView = useRequireLoginRedirect();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<AppMyStoreOrderRow[]>([]);

  const load = useCallback(async () => {
    if (!canView) {
      return;
    }
    setLoading(true);
    const res = await StoreOrderApi.listMyOrders({
      pageNum: 1,
      pageSize: 100,
    });
    setLoading(false);
    if (typeof res === "string") {
      return;
    }
    setRows(filterOrdersWithinThreeYears(res.list ?? []));
  }, [canView]);

  useEffect(() => {
    void load();
  }, [load]);

  const goOrderDetail = (row: AppMyStoreOrderRow) => {
    if (!spaceId) {
      return;
    }
    const path = resolveH5StoreOrderDetailPath(spaceId, row.orderId);
    const sid = row.spaceId.trim();
    h5NavigateTo(navigate, sid && sid !== spaceId ? `${path}?orderSpaceId=${encodeURIComponent(sid)}` : path);
  };

  const backTo = spaceId ? `/space/${spaceId}/mine` : undefined;

  return (
    <H5SubPageShell title="我的订单" backTo={backTo}>
      <div className="space-mine-orders" style={spaceHomeOrderListCssVars()}>
        <Spin spinning={loading}>
          {!loading && rows.length === 0 ? (
            <p className="h5-sub-page__empty">近 3 年内暂无订单</p>
          ) : (
            <ul className="space-mine-orders__list">
              {rows.map((row) => {
                const unpaid = isUnpaidStoreOrder(row);
                const amount = unpaid ? row.productPriceYuan : row.payAmountYuan;
                const posterUrl = resolveAppMediaUrl(row.productPosterUrl?.trim() || null);
                return (
                  <li key={row.orderId} className="space-mine-orders__card">
                    <button
                      type="button"
                      className="space-mine-orders__card-main"
                      onClick={() => goOrderDetail(row)}
                    >
                    <div className="space-mine-orders__shop-row">
                      <div className="space-mine-orders__shop-left">
                        <span className="space-mine-orders__shop-name">{row.spaceName}</span>
                        <RightOutlined className="space-mine-orders__shop-arrow" aria-hidden />
                      </div>
                      <span className={resolveStoreOrderStatusClass(row)}>
                        {row.displayStatusLabel}
                      </span>
                    </div>

                    <div className="space-mine-orders__product">
                      <div className="space-mine-orders__poster">
                        {posterUrl ? (
                          <img src={posterUrl} alt="" className="space-mine-orders__poster-img" />
                        ) : (
                          <span className="space-mine-orders__poster-placeholder" aria-hidden />
                        )}
                      </div>
                      <div className="space-mine-orders__product-main">
                        <div className="space-mine-orders__product-title-row">
                          <p className="space-mine-orders__product-name">{row.productName}</p>
                          <span className="space-mine-orders__product-price space-mine-orders__fin-num">
                            ¥{amount.toFixed(2)}
                          </span>
                        </div>
                        <p className="space-mine-orders__product-spec">
                          {formatOrderSpecLabel(row)}
                        </p>
                        <p className="space-mine-orders__product-time">
                          下单 {formatApiDateTime(row.createdAt)}
                        </p>
                      </div>
                    </div>

                    <div className="space-mine-orders__total-row">
                      <span className="space-mine-orders__total">
                        合计{" "}
                        <strong className="space-mine-orders__total-amount space-mine-orders__fin-num">
                          ¥{amount.toFixed(2)}
                        </strong>
                      </span>
                    </div>
                    </button>

                    {unpaid ? (
                      <div className="space-mine-orders__actions">
                        <button
                          type="button"
                          className="space-mine-orders__pay-btn"
                          onClick={() => goOrderDetail(row)}
                        >
                          立即支付
                        </button>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </Spin>
      </div>
    </H5SubPageShell>
  );
});

export default SpaceMineOrdersPage;
